import { AHSeparator, AHText, Box, ListItem } from "arkh";
import { useLazyLoadQuery, graphql } from "react-relay";
import { FlatList } from "react-native";

import React, { useCallback, useMemo, useState } from "react";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";

import { OrgSearchInput } from "../../OrgSearchInput";
import { SortableColumnTitle } from "../../SortableColumnTitle";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { listGutterStyle } from "../../guterStyle";
import { useOrgHomeDetailLayout } from "../../useOrgHomeDetailContainerStyle";
import { OrgAdminScreenProps } from "../OrgAdmin";
import { OrgAdminDevicesScreenQuery } from "./__generated__/OrgAdminDevicesScreenQuery.graphql";
import { SegmentedControlNew } from "@/src/components/SegmentedControlNew/SegmentedControlNew";
import { OrgScreenListItem } from "./OrgScreenListItem";
import { OrgMobileDeviceListItem } from "./OrgMobileDeviceListItem";

type DeviceType = "SCREENS" | "SHARED_DEVICES";
type MobileDeviceOrderBy = "status" | "name";

function InnerOrgDevicesScreen(
  props: OrgAdminScreenProps<"OrgAdminDevices"> & { fetchKey: string }
) {
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState<DeviceType>("SCREENS");
  const [orderBy, setOrderBy] = useState<MobileDeviceOrderBy>("status");
  const [order, setOrder] = useState<"ASC" | "DESC">("DESC");
  const intl_tokens = useIntlTokens();

  const data = useLazyLoadQuery<OrgAdminDevicesScreenQuery>(
    graphql`
      query OrgAdminDevicesScreenQuery {
        organisation @required(action: THROW) {
          id
          storecastScreens(first: 1000) {
            edges {
              node {
                id
                name
                lastIPConformsToCIDR
                lastUsedAtDate {
                  isoString
                }
                store {
                  id
                  name
                  externalId
                }
                ...OrgScreenListItem_screen
              }
            }
          }
          sharedMobileDevices(first: 1000) {
            edges {
              node {
                id
                name
                lastIPConformsToCIDR
                lastUsedAtDate {
                  isoString
                }
                store {
                  id
                  name
                  externalId
                }
                ...OrgMobileDeviceListItem_device
              }
            }
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const screens = useMemo(() => {
    const items =
      data.organisation.storecastScreens?.edges
        .map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => n != null) ?? [];

    const sorted = [...items].sort((a, b) => {
      let result = 0;
      if (orderBy === "status") {
        const cidrOrder = (v: boolean | null | undefined) =>
          v === false ? 0 : v == null ? 1 : 2;
        result =
          cidrOrder(a.lastIPConformsToCIDR) -
          cidrOrder(b.lastIPConformsToCIDR);
        if (result === 0) {
          const dateA = a.lastUsedAtDate?.isoString ?? "";
          const dateB = b.lastUsedAtDate?.isoString ?? "";
          result = dateB.localeCompare(dateA);
        }
      } else {
        result = a.name.localeCompare(b.name);
      }
      return order === "ASC" ? -result : result;
    });

    return sorted;
  }, [data.organisation.storecastScreens, orderBy, order]);

  const mobileDevices = useMemo(() => {
    const devices =
      data.organisation.sharedMobileDevices?.edges
        .map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => n != null) ?? [];

    const sorted = [...devices].sort((a, b) => {
      let result = 0;
      if (orderBy === "status") {
        // Sort by lastIPConformsToCIDR: false (non-conforming) first, then null, then true
        const cidrOrder = (v: boolean | null) =>
          v === false ? 0 : v == null ? 1 : 2;
        result = cidrOrder(a.lastIPConformsToCIDR) - cidrOrder(b.lastIPConformsToCIDR);
        if (result === 0) {
          // Secondary sort by lastUsedAtDate descending
          const dateA = a.lastUsedAtDate?.isoString ?? "";
          const dateB = b.lastUsedAtDate?.isoString ?? "";
          result = dateB.localeCompare(dateA);
        }
      } else {
        result = a.name.localeCompare(b.name);
      }
      return order === "ASC" ? -result : result;
    });

    return sorted;
  }, [data.organisation.sharedMobileDevices, orderBy, order]);

  const filterBySearch = useCallback(
    <
      T extends {
        name: string;
        lastIPConformsToCIDR?: boolean | null;
        store: { id: string; name: string; externalId: string | null } | null;
      },
    >(
      devices: T[]
    ): T[] => {
      if (search.length === 0) {
        return devices;
      }
      const normalizedSearch = search.toLocaleLowerCase();

      return devices.filter((d) => {
        if (
          normalizedSearch.startsWith("nonconform") &&
          d.lastIPConformsToCIDR === false
        ) {
          return true;
        }
        if (
          normalizedSearch.startsWith("conform") &&
          d.lastIPConformsToCIDR === true
        ) {
          return true;
        }
        if (d.name.toLocaleLowerCase().includes(normalizedSearch)) {
          return true;
        }
        if (d.store?.name?.toLocaleLowerCase().includes(normalizedSearch)) {
          return true;
        }
        if (d.store?.id?.toLocaleLowerCase().includes(normalizedSearch)) {
          return true;
        }
        if (
          d.store?.externalId?.toLocaleLowerCase().includes(normalizedSearch)
        ) {
          return true;
        }
        return false;
      });
    },
    [search]
  );

  const filteredScreens = useMemo(
    () => filterBySearch(screens),
    [filterBySearch, screens]
  );

  const filteredMobileDevices = useMemo(
    () => filterBySearch(mobileDevices),
    [filterBySearch, mobileDevices]
  );

  const renderScreenItem = useCallback(
    ({ item, index }: { item: (typeof screens)[number]; index: number }) => {
      return (
        <OrgScreenListItem
          screen={item}
          isLast={index === filteredScreens.length - 1}
        />
      );
    },
    [filteredScreens.length]
  );

  const renderMobileDeviceItem = useCallback(
    ({
      item,
      index,
    }: {
      item: (typeof mobileDevices)[number];
      index: number;
    }) => {
      return (
        <OrgMobileDeviceListItem
          device={item}
          isLast={index === filteredMobileDevices.length - 1}
        />
      );
    },
    [filteredMobileDevices.length]
  );

  const detailLayout = useOrgHomeDetailLayout();

  const listHeader = (
    <Box color="surface-background-light" spaceTop="medium">
      <Box
        style={{
          flexDirection: "row",
          justifyContent: "center",
        }}
        spaceBottom="medium"
      >
        <SegmentedControlNew
          items={[
            {
              children: <AHText type="subtitle">{intl_tokens.Screens}</AHText>,
              value: "SCREENS" as DeviceType,
              width: 120,
            },
            {
              children: (
                <AHText type="subtitle">{intl_tokens.SharedDevices}</AHText>
              ),
              value: "SHARED_DEVICES" as DeviceType,
              width: 140,
            },
          ]}
          selected={selectedType}
          setSelected={setSelectedType}
        />
      </Box>
      <OrgSearchInput
        placeholder={intl_tokens.Search}
        value={search}
        onChangeText={setSearch}
      />
      <Box color="surface-background-light">
        <ListItem disabled displayMode="TABLE" border="border-primary">
          <Box style={{ flexDirection: "row", flex: 1 }}>
            <Box style={{ width: "35%" }}>
              <SortableColumnTitle
                title={intl_tokens.Status}
                onOrderBySelect={() => setOrderBy("status")}
                order={orderBy === "status" ? order : null}
                onOrderChange={setOrder}
              />
            </Box>
            <Box style={{ width: "35%" }}>
              <SortableColumnTitle
                title={intl_tokens.Name}
                onOrderBySelect={() => setOrderBy("name")}
                order={orderBy === "name" ? order : null}
                onOrderChange={setOrder}
              />
            </Box>
          </Box>
        </ListItem>
        <AHSeparator />
      </Box>
    </Box>
  );

  const listStyle = [
    {
      flex: 1,
      marginBottom: detailLayout.keyboardBottomInset,
    },
    listGutterStyle,
  ];

  const listContentStyle = [
    detailLayout.contentContainerStyle,
    { minWidth: 300 },
  ];

  return (
    <React.Fragment>
      {selectedType === "SCREENS" ? (
        <FlatList
          style={listStyle}
          contentContainerStyle={listContentStyle}
          data={filteredScreens}
          keyboardShouldPersistTaps="handled"
          renderItem={renderScreenItem}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={AHSeparator}
          ListEmptyComponent={WorkspaceEmptyListItem}
          stickyHeaderIndices={[0]}
          ListHeaderComponent={listHeader}
        />
      ) : (
        <FlatList
          style={listStyle}
          contentContainerStyle={listContentStyle}
          data={filteredMobileDevices}
          keyboardShouldPersistTaps="handled"
          renderItem={renderMobileDeviceItem}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={AHSeparator}
          ListEmptyComponent={WorkspaceEmptyListItem}
          stickyHeaderIndices={[0]}
          ListHeaderComponent={listHeader}
        />
      )}
    </React.Fragment>
  );
}

export function OrgAdminDevicesScreen(
  props: OrgAdminScreenProps<"OrgAdminDevices">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey: string) => (
        <InnerOrgDevicesScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
